import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { LatLng } from "use-places-autocomplete";
import { PagesEnum } from "../../../utils/enums";
import { Address, FranchiseDeliveryConfig } from "../../../utils/types";

export interface InitialState {
  savedAddresses: Address[];
  selectedAddress: Address | null;
  /**
   * WHICH config the guest picked -- not the config itself.
   *
   * The whole slice is persisted to localStorage, so anything server-shaped
   * kept here is a copy that goes stale the moment ops edits it and never
   * heals. The config, its zones, the fee, the ETAs and the zone id are all
   * derived from `selectedFranchise.franchiseConfig.deliveryV2` instead, which
   * is rebuilt from the page props on every load. See
   * `redux/selectors/deliverySelectors.ts`. prepits/project#4204
   */
  currentDeliveryConfigId: number | null;
  backPage: PagesEnum;
  tempLocation: LatLng;
}

const initialState: InitialState = {
  savedAddresses: [],
  currentDeliveryConfigId: null,
  backPage: PagesEnum.Home,
  tempLocation: {
    lat: 0,
    lng: 0,
  },
  selectedAddress: null,
};

const config = createSlice({
  name: "delivery",
  initialState,
  reducers: {
    /**
     * Still takes the whole config, and only keeps its id.
     *
     * The payload shape is deliberately unchanged: every caller already has the
     * config object in hand, the action name is what
     * cartActionV2Middleware watches to recalculate, and narrowing the
     * signature would have churned twenty call sites for nothing. What changed
     * is that the object is no longer stored -- the id is enough to find it
     * again in the franchise the page carries.
     *
     * Clearing still clears the address: choosing no delivery type means the
     * address that was picked under it no longer applies.
     */
    setCurrentDeliveryConfig: (
      state,
      action: PayloadAction<FranchiseDeliveryConfig | null>
    ) => {
      state.currentDeliveryConfigId = action.payload?.id ?? null;

      if (!action.payload) {
        state.selectedAddress = null;
      }
    },

    setSavedAddresses: (state, action) => {
      state.savedAddresses = action.payload;
    },

    setSelectedAddress: (state, action) => {
      state.selectedAddress = action.payload;
    },
    setTempLocation: (state, action) => {
      state.tempLocation = action.payload;
    },

    setBackPage: (state, action) => {
      state.backPage = action.payload;
    },
  },
});

export const {
  setSavedAddresses,
  setSelectedAddress,
  setCurrentDeliveryConfig,
  setTempLocation,
  setBackPage,
} = config.actions;

export default config.reducer;
